// Model cashflow (hlášení #188, 1. 11. 2026). Záložka Model na stránce Cashflow.
//
// Jeden trvalý model, ne po měsících. Prázdné pole počítá s průměrem
// skutečnosti (posledních nejvýš 12 ukončených měsíců, aspoň 3), napsané číslo
// se uloží a drží, dokud ho majitel sám nevrátí k průměru: u řádku, u sekce,
// nebo celé najednou. Skutečnosti ani transakcí se model nedotýká.
// Princip převzatý z PM (ModelMesic), backend v _lib/cashflowModel.cjs.
//
// Vracení k průměru (2. 11. 2026, podle zadání pro PM): v klidu u řádků žádná
// tlačítka, jen čísla. Sekce s vlastním číslem má v pruhu kulatou ikonku ↺.
// Klepnutí ji odkryje (jen jednu sekci najednou): u upravených řádků se objeví
// čtvercové ↺ (vrátí řádek hned), vedle ikonky „Vrátit celou sekci“ (s potvrzením)
// a ikonka se změní na plné kolečko s ✕, které sekci zavře.

// Odloženo do majetku (dlaždice z PM) tu schválně není: admin nemá kategorii,
// která by peníze odkládala mimo provoz. Loans & Investments je externí
// financování (půjčky, vklady) a do modelu nepatří, rezervy by byly provozní
// náklad, který už je v Nákladech.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, RotateCcw, Check, RefreshCw, TrendingUp, TrendingDown, X } from "lucide-react";
import { KartaPrehledu, HlavickaDlazdice } from "../components/PrehledDlazdice.jsx";
import { Hlaska, AkceZnovu } from "../components/ui.jsx";
import { confirmDialog } from "../components/confirmHost.jsx";
import { toast } from "../components/Toast.jsx";
import { apiFetch, fetchApi } from "../lib/api.js";
import { fmt } from "../lib/format.js";

/** „1 vlastní číslo se smaže“, „2 vlastní čísla se smažou“, „5 vlastních čísel se smaže“. */
function vlastniCislaSeSmazou(n) {
  if (n === 1) return "1 vlastní číslo se smaže";
  if (n >= 2 && n <= 4) return `${n} vlastní čísla se smažou`;
  return `${n} vlastních čísel se smaže`;
}
const MESICE_KRATCE = ["led", "úno", "bře", "dub", "kvě", "čer", "čvc", "srp", "zář", "říj", "lis", "pro"];
const kc = (n) => `${fmt(Math.round(Number(n) || 0))} Kč`;
const znamenko = (n) => (n > 0 ? "+" : n < 0 ? "−" : "±");
const klic = (l1, l2) => `${l1}\u0000${l2}`;

function mesicKratce(mk) {
  const [y, m] = String(mk || "").split("-");
  return m ? `${MESICE_KRATCE[parseInt(m, 10) - 1]} ${y}` : "";
}

/** "150 000", "150000,5", "150 000 Kč" → číslo; "" → null; nesmysl → NaN. */
function prevedCislo(text) {
  const t = String(text ?? "").replace(/kč/gi, "").replace(/[\s ]/g, "").replace(",", ".").replace(/^[\u2212\u2013]/, "-");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** "za 10 posledních měsíců", "za 3 poslední měsíce". */
function zaMesicu(n) {
  if (n === 1) return "za poslední měsíc";
  if (n >= 2 && n <= 4) return `za ${n} poslední měsíce`;
  return `za ${n} posledních měsíců`;
}

/**
 * Pole s částkou. Řízené: dokud se do něj píše, zvenku se nepřepisuje,
 * uloží se po opuštění pole nebo Enteru. Escape vrátí původní hodnotu.
 * Průměr je šedý placeholder a je i v nápovědě pole.
 */
function ModelPole({ hodnota, prumer, popis, upraveno, onUloz }) {
  const vychozi = hodnota === null ? "" : fmt(hodnota);
  const [text, setText] = useState(vychozi);
  const [pisu, setPisu] = useState(false);
  useEffect(() => { if (!pisu) setText(vychozi); }, [vychozi, pisu]);
  const prumerText = prumer === null ? "bez průměru" : `průměr ${kc(prumer)}`;

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={prumer === null ? "0" : fmt(Math.round(prumer))}
      aria-label={`Model, ${popis}`}
      title={upraveno
        ? `Tvoje číslo (${prumerText}). Smaž ho a vrátí se průměr.`
        : `Šedé číslo je ${prumerText}. Napiš vlastní číslo a model si ho zapamatuje.`}
      onFocus={(e) => { setPisu(true); if (hodnota !== null) setText(String(hodnota)); requestAnimationFrame(() => e.target.select()); }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { setPisu(false); onUloz(text); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setText(hodnota === null ? "" : String(hodnota)); requestAnimationFrame(() => e.target.blur()); }
      }}
      className={`h-10 w-[104px] shrink-0 rounded-lg border px-2.5 text-right text-base tabular-nums demo-sensitive outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 sm:h-9 sm:w-[120px] sm:text-[15px] ${
        upraveno
          ? "border-brand bg-white font-bold text-brand-950 shadow-sm"
          : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400"
      }`}
      data-model-pole={popis}
    />
  );
}

/**
 * Jeden řádek modelu, kompaktní linka jako v PM: název vlevo, pole vpravo.
 * V klidu bez tlačítek. Když je sekce odkrytá, dostane upravený řádek vedle
 * pole čtvercové ↺ (vrátí ho k průměru bez ptaní), ostatní řádky té sekce
 * prázdné místo stejné šířky, ať pole lícují. Dlouhý název se zalomí, neuřízne.
 */
function ModelRadek({ polozka, stav, odkryto, pracuji, onUloz, onPrumer }) {
  const upraveno = polozka.castka !== null;
  const nazev = polozka.l2 || "Bez podkategorie";
  return (
    <div className="flex items-center gap-2 border-t border-slate-200 px-3 py-1.5 first:border-t-0 sm:px-4" data-model-radek={nazev}>
      <span className="min-w-0 flex-1 break-words text-sm leading-snug text-slate-800">{nazev}</span>
      {stav === "uklada" && (
        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 p-1 text-slate-500" title="Ukládám">
          <RefreshCw className="h-3 w-3 animate-spin" />
        </span>
      )}
      {stav === "ulozeno" && (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          <Check className="h-3 w-3" /> uloženo
        </span>
      )}
      <ModelPole
        hodnota={polozka.castka}
        prumer={polozka.prumer}
        popis={nazev}
        upraveno={upraveno}
        onUloz={onUloz}
      />
      {odkryto && (upraveno ? (
        <button
          type="button"
          onClick={onPrumer}
          disabled={pracuji}
          title="Vrátit k průměru"
          aria-label={`Vrátit k průměru: ${nazev}`}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 transition active:scale-95 disabled:opacity-50 sm:h-9 sm:w-9 sm:hover:border-brand sm:hover:text-brand-deep"
          data-model-vratit-radek={nazev}
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      ) : (
        <span className="h-10 w-10 shrink-0 sm:h-9 sm:w-9" aria-hidden="true" />
      ))}
    </div>
  );
}

function Souhrn({ popisek, hodnota, prumer, ton, maPrumer, lepsiVyssi = true, className = "" }) {
  const rozdil = maPrumer ? hodnota - prumer : 0;
  const tony = {
    zelena: "border-emerald-200 bg-emerald-50 text-emerald-900",
    cervena: "border-rose-200 bg-rose-50 text-rose-900",
    znacka: "border-brand/30 bg-brand-light/60 text-brand-950",
    minus: "border-rose-300 bg-rose-100 text-rose-900",
  };
  const dobre = lepsiVyssi ? rozdil >= 0 : rozdil <= 0;
  return (
    <div className={`min-w-0 rounded-xl border px-3 py-2.5 ${tony[ton]} ${className}`} data-model-souhrn={popisek}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-70">{popisek}</div>
      <div className="mt-0.5 whitespace-nowrap text-lg font-bold leading-tight tabular-nums sm:text-xl demo-sensitive">{kc(hodnota)}</div>
      {maPrumer && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600">
            průměr&nbsp;<span className="demo-sensitive">{kc(prumer)}</span>
          </span>
          {Math.round(rozdil) !== 0 && (
            <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tabular-nums ${dobre ? "text-emerald-700" : "text-rose-700"}`}>
              {rozdil > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              <span className="demo-sensitive">{znamenko(rozdil)}{kc(Math.abs(rozdil))}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function CashflowModel() {
  const [model, setModel] = useState(null);
  const [chyba, setChyba] = useState("");
  const [stavy, setStavy] = useState({}); // klic → "uklada" | "ulozeno"
  const [pracuji, setPracuji] = useState(false);
  const [otevrena, setOtevrena] = useState(null); // l1 odkryté sekce, najednou jen jedna

  const nacti = useCallback(async () => {
    setChyba("");
    try {
      const d = await fetchApi("/api/cashflow?action=model");
      setModel(d.model);
    } catch (e) {
      setChyba(e.message || "Nepodařilo se načíst model.");
    }
  }, []);
  useEffect(() => { nacti(); }, [nacti]);

  const post = async (body) => {
    const r = await apiFetch("/api/cashflow", { method: "POST", body: JSON.stringify(body) });
    const d = await r.json().catch(() => null);
    if (!r.ok || !d || d.ok !== true) throw new Error(d?.error || `chyba serveru ${r.status}`);
    return d;
  };

  const nastavCastku = (l1, l2, castka) => setModel((m) => m && ({
    ...m,
    sekce: m.sekce.map((s) => (s.l1 !== l1 ? s : {
      ...s,
      polozky: s.polozky.map((p) => (p.l2 !== l2 ? p : { ...p, castka })),
    })),
  }));

  const oznac = (k, stav) => {
    setStavy((s) => ({ ...s, [k]: stav }));
    if (stav === "ulozeno") setTimeout(() => setStavy((s) => (s[k] === "ulozeno" ? { ...s, [k]: undefined } : s)), 2000);
  };

  async function ulozRadek(polozka, text) {
    const castka = prevedCislo(text);
    if (Number.isNaN(castka)) {
      toast(`„${text}" není číslo, nechávám původní hodnotu.`, "error");
      return;
    }
    const nova = castka === null ? null : Math.round(castka * 100) / 100;
    if (nova === polozka.castka) return; // beze změny se neukládá
    const k = klic(polozka.l1, polozka.l2);
    const puvodni = polozka.castka;
    nastavCastku(polozka.l1, polozka.l2, nova);
    oznac(k, "uklada");
    try {
      await post({ action: "modelUloz", bunky: [{ l1: polozka.l1, l2: polozka.l2, castka: nova }] });
      oznac(k, "ulozeno");
    } catch (e) {
      nastavCastku(polozka.l1, polozka.l2, puvodni);
      oznac(k, undefined);
      toast(`Neuloženo: ${e.message}`, "error");
    }
  }

  async function reset(body, hlaska) {
    setPracuji(true);
    try {
      const d = await post(body);
      setModel(d.model);
      toast(hlaska, "success");
    } catch (e) {
      toast(`Nepovedlo se: ${e.message}`, "error");
    } finally {
      setPracuji(false);
    }
  }

  async function resetVse() {
    const ok = await confirmDialog({
      title: "Vrátit celý model k průměru?",
      description: `${vlastniCislaSeSmazou(upravenoCelkem)} a vrátí k průměru. Skutečnosti ani transakcí se to nedotkne.`,
      confirmLabel: "Ano, vrátit vše",
    });
    if (!ok) return;
    setOtevrena(null);
    reset({ action: "modelResetVse" }, "Model je zase celý podle průměru.");
  }

  async function resetSekce(l1, pocet) {
    const ok = await confirmDialog({
      title: `Vrátit sekci ${l1} k průměru?`,
      description: `${vlastniCislaSeSmazou(pocet)} a vrátí k průměru.`,
      confirmLabel: "Ano, vrátit sekci",
    });
    if (!ok) return;
    reset({ action: "modelResetSekce", l1 }, `${l1}: zpět na průměr.`);
  }

  const castka = (p) => (p.castka !== null ? p.castka : p.prumer || 0);
  const souctyMemo = useMemo(() => {
    const vys = { prijmy: 0, naklady: 0, prijmyPrumer: 0, nakladyPrumer: 0, upraveno: 0 };
    for (const s of model?.sekce || []) {
      for (const p of s.polozky) {
        const cil = s.druh === "prijem" ? "prijmy" : "naklady";
        vys[cil] += castka(p);
        vys[`${cil}Prumer`] += p.prumer || 0;
        if (p.castka !== null) vys.upraveno++;
      }
    }
    return vys;
  }, [model]);
  const upravenoCelkem = souctyMemo.upraveno;

  // Po vrácení posledního vlastního čísla se odkrytá sekce sama zavře.
  const upravenoOtevrene = otevrena
    ? (model?.sekce.find((s) => s.l1 === otevrena)?.polozky || []).filter((p) => p.castka !== null).length
    : 0;
  useEffect(() => {
    if (otevrena && upravenoOtevrene === 0) setOtevrena(null);
  }, [otevrena, upravenoOtevrene]);

  if (chyba) {
    return (
      <Hlaska ton="problem" title="Model se nenačetl" akce={<AkceZnovu onClick={nacti} />}>{chyba}</Hlaska>
    );
  }
  if (!model) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        <span className="ml-3 text-sm text-slate-500">Načítám model…</span>
      </div>
    );
  }

  const { prijmy, naklady, prijmyPrumer, nakladyPrumer } = souctyMemo;
  const vysledek = prijmy - naklady;
  const vysledekPrumer = prijmyPrumer - nakladyPrumer;
  const obdobi = model.mesice.length ? `${mesicKratce(model.mesice[0])} až ${mesicKratce(model.mesice[model.mesice.length - 1])}` : "";

  // Ovládání vracení pro sekci (L1): v klidu kulatá ikonka ↺ s rámečkem,
  // odkrytá sekce má vedle „Vrátit celou sekci“ a místo ↺ plné kolečko s ✕.
  const ovladaniSekce = (l1, pocet) => {
    const odkryto = otevrena === l1;
    return (
      <span className="flex shrink-0 items-center gap-1.5" data-model-ovladani={l1}>
        {odkryto && (
          <button
            type="button"
            disabled={pracuji}
            onClick={() => resetSekce(l1, pocet)}
            className="inline-flex h-9 items-center whitespace-nowrap rounded-full border border-brand/40 bg-white px-3 text-xs font-semibold text-brand-deep transition active:scale-95 disabled:opacity-50 sm:h-7 sm:hover:border-brand"
            data-model-vratit-sekci={l1}
          >
            Vrátit celou sekci
          </button>
        )}
        <button
          type="button"
          onClick={() => setOtevrena(odkryto ? null : l1)}
          aria-expanded={odkryto}
          title={odkryto ? "Zavřít vracení k průměru" : `Vracení k průměru v sekci ${l1}`}
          aria-label={odkryto ? `Zavřít vracení k průměru: ${l1}` : `Vracení k průměru: ${l1}`}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition active:scale-95 sm:h-7 sm:w-7 ${
            odkryto
              ? "border-brand bg-brand text-white sm:hover:bg-brand-deep"
              : "border-slate-300 bg-white text-slate-600 sm:hover:border-brand sm:hover:text-brand-deep"
          }`}
          data-model-ikonka={l1}
        >
          {odkryto ? <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> : <RotateCcw className="h-4 w-4 sm:h-3.5 sm:w-3.5" />}
        </button>
      </span>
    );
  };

  // Blok PŘÍJMY / NÁKLADY: malý barevný nadpis a pod ním jedna karta s řádky.
  // Skupiny (L1) jsou šedé pruhy se součtem. Když je v bloku jediná skupina
  // příjmů, pruh se nekreslí (jako v PM) a tlačítko k průměru je u nadpisu.
  const blok = (nadpis, ton, seznam, sPruhy) => {
    const jedina = !sPruhy && seznam.length === 1 ? seznam[0] : null;
    const upravenoJedina = jedina ? jedina.polozky.filter((p) => p.castka !== null).length : 0;
    return (
      <div data-model-blok={nadpis}>
        <div className="mb-1.5 flex min-h-[24px] items-center justify-between gap-2">
          <p className={`text-xs font-bold uppercase tracking-wide ${ton}`}>{nadpis}</p>
          {jedina && upravenoJedina > 0 && ovladaniSekce(jedina.l1, upravenoJedina)}
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {seznam.map((s) => {
            const soucet = s.polozky.reduce((a, p) => a + castka(p), 0);
            const upravenoSekce = s.polozky.filter((p) => p.castka !== null).length;
            return (
              <div key={s.l1} data-model-sekce={s.l1}>
                {!jedina && (
                  <div className="flex min-h-[36px] items-center gap-2 border-t border-slate-200 bg-slate-100 px-3 py-1 first:border-t-0 sm:min-h-[34px] sm:px-4" data-model-pruh={s.l1}>
                    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
                      <span className="min-w-0 break-words text-xs font-bold text-brand-950">{s.l1}</span>
                      <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-slate-500 demo-sensitive">{kc(soucet)}</span>
                    </span>
                    {upravenoSekce > 0 && ovladaniSekce(s.l1, upravenoSekce)}
                  </div>
                )}
                {s.polozky.map((radek) => {
                  // Skupina se bere ze sekce, ať řádek nikdy neodejde bez ní.
                  const p = { ...radek, l1: s.l1 };
                  return (
                    <ModelRadek
                      key={p.l2}
                      polozka={p}
                      stav={stavy[klic(p.l1, p.l2)]}
                      odkryto={otevrena === s.l1}
                      pracuji={pracuji}
                      onUloz={(text) => ulozRadek(p, text)}
                      onPrumer={() => ulozRadek(p, "")}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const sekcePrijmy = model.sekce.filter((s) => s.druh === "prijem");
  const sekceNaklady = model.sekce.filter((s) => s.druh === "naklad");

  return (
    <div data-cashflow-model>
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={SlidersHorizontal}
          nazev="Model měsíce"
          podtitulek="tvoje čísla platí, dokud je nevrátíš k průměru"
          napoveda="Model je hra na co kdyby. Nesahá na skutečnost ani na roční tabulku. Šedá čísla jsou průměr skutečnosti, přepiš, co by bylo jinak, a hned uvidíš výsledek měsíce."
        />
        <div className="space-y-4 p-3 sm:p-4">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Souhrn popisek="Příjmy" hodnota={prijmy} prumer={prijmyPrumer} ton="zelena" maPrumer={model.maPrumer} />
              <Souhrn popisek="Náklady" hodnota={naklady} prumer={nakladyPrumer} ton="cervena" maPrumer={model.maPrumer} lepsiVyssi={false} />
              <Souhrn popisek="Výsledek" hodnota={vysledek} prumer={vysledekPrumer} ton={vysledek >= 0 ? "znacka" : "minus"} maPrumer={model.maPrumer} className="col-span-2 sm:col-span-1" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p
                className="min-w-0 flex-1 basis-64 text-xs text-slate-500"
                title={model.maPrumer ? `Průměr se bere z měsíců ${obdobi}. Rozběhnutý měsíc se nepočítá.` : undefined}
                data-model-zdroj
              >
                {model.maPrumer
                  ? `Šedá čísla jsou průměr ${zaMesicu(model.mesicu)} se skutečností. Přepiš, co bude jinak. Tvoje čísla zůstanou, dokud je nevrátíš k průměru.`
                  : `Historie je zatím krátká (${model.mesicu} měs.), průměr bude až od ${model.minMesicu} měsíců. Vyplň čísla ručně.`}
              </p>
              {upravenoCelkem > 0 && (
                <button
                  type="button"
                  onClick={resetVse}
                  disabled={pracuji}
                  className="btn btn-secondary btn-sm shrink-0 gap-1.5"
                  data-model-vratit-vse
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Vrátit vše k průměru
                </button>
              )}
            </div>
            {!model.maPrumer && (
              <Hlaska ton="pozor" title="Průměr zatím není">
                Skutečnost je zatím jen za {model.mesicu} měs., z toho se rozumný průměr udělat nedá. Vyplň čísla ručně.
              </Hlaska>
            )}
          </div>

          {sekcePrijmy.length > 0 && blok("Příjmy", "text-emerald-700", sekcePrijmy, sekcePrijmy.length > 1)}
          {sekceNaklady.length > 0 && blok("Náklady", "text-rose-700", sekceNaklady, true)}
        </div>
      </KartaPrehledu>
    </div>
  );
}
